<template>
  <div class="evaluation-chart-suggestions">
    <h2 class="label">{{ $t("labels.chartType") }}</h2>
    <div class="evaluation-chart-suggestions__grid pb-4">
      <button
        v-for="chart in charts"
        :key="chart.id"
        type="button"
        class="evaluation-chart__typeoption"
        @click="fillByChartType(chart.id)"
      >
        <lucide-icon :name="chart.icon" class="evaluation-chart__typeicon" />
        <span class="ml-1">
          {{ $t(`chartTypes.${chart.id}`) }}
        </span>
      </button>
    </div>

    <slot></slot>

    <h2 class="label">{{ $t("headings.suggestions") }}</h2>
    <div class="evaluation-chart-suggestions__grid">
      <slot name="grid"></slot>

      <button
        v-for="suggestion of diagramConfigurationSuggestions"
        :key="suggestion.id"
        type="button"
        class="evaluation-chart-suggestions__suggestion"
        @click="fillBySuggestion(suggestion)"
      >
        <img v-if="suggestion.img" :src="suggestion.img" alt="" />
        <div>{{ suggestion.title }}</div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";

import { ensureInt } from "@/helpers/utility/Type";
import { useSimulationStore } from "../simulation/simulations.store";
import { useActionStore } from "../action/actions.store";
import { useTargetStore } from "../target/targets.store";
import { useReactiveChartData } from "../charts/useChartData";
import { useEvaluationStore } from "./evaluations.store";
import { charts } from "../charts/charts";

import type { Subset } from "@/helpers/utility/Type";
import type { ChartType } from "../charts/ChartItem";
import type { EvaluationItem } from "./EvaluationItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import type { SimulationItem } from "../simulation/SimulationItem";

type DiagramConfigurationSuggestion = {
  id: string;
  title: string;
  chartType: ChartType;
  img?: string;
  getDefaults: (evaluation?: EvaluationItem) => Subset<EvaluationItem>;
};

const props = defineProps<{
  evaluationId: number | null;
}>();

const emit = defineEmits<{
  (e: "choose", evaluation: EvaluationItem): void;
}>();

const { t } = useI18n();
const evaluationStore = useEvaluationStore();
const simulationStore = useSimulationStore();

const evaluation = computed(() => {
  if (props.evaluationId == null) return null;
  return evaluationStore.items[props.evaluationId];
});

const ctx = useReactiveChartData(evaluation);

/**
 * The best simulation to use for the suggestions.
 * Either the simulation of the evaluation, or the last one created.
 */
const bestSimulation = computed((): SimulationItem | undefined => {
  if (evaluation.value == null) {
    return simulationStore.orderedItems[simulationStore.orderedItems.length - 1];
  }
  return (
    ctx.selectedSimulation ||
    ctx.selectedSimulations?.[0] ||
    simulationStore.orderedItems[simulationStore.orderedItems.length - 1]
  );
});

async function fillBySuggestion(suggestion: DiagramConfigurationSuggestion) {
  const evaluation = await getEvaluation();
  evaluationStore.setChartType(evaluation.id, suggestion.chartType);

  const defaults = suggestion.getDefaults(evaluation);

  evaluationStore.update({
    id: evaluation.id,
    ...defaults
  });

  evaluationStore.updateAllSimulationIcons(evaluation.id);
  evaluationStore.updateRoundsToSimulations(evaluation.id);

  emit("choose", evaluation);
}

async function fillByChartType(type: ChartType) {
  const evaluation = await getEvaluation();
  evaluationStore.setChartType(evaluation.id, type);
  evaluationStore.updateAllSimulationIcons(evaluation.id);

  emit("choose", evaluation);
}

async function getEvaluation() {
  if (props.evaluationId == null) {
    return await evaluationStore.create();
  }
  return evaluationStore.items[props.evaluationId];
}

// ===================
// ALL SUGGESTIONS

const diagramConfigurationSuggestions = computed(() => {
  const simulation = bestSimulation.value;

  const rounds = simulation ? simulation.rounds.filter((r) => r.played) : [];
  const lastRound: SimulationRoundItem | undefined = rounds[rounds.length - 1];

  const suggestions: DiagramConfigurationSuggestion[] = [
    {
      id: "all-simulations",
      title: t("chartSuggestions.allSimulations"),
      chartType: "line",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Cpath d='M10,50L50,30L120,90L190,20' stroke='%23000' fill='none' %3E%3C/path%3E%3Cpath d='M10,50L40,70L160,30L190,50' stroke='%23000' fill='none' %3E%3C/path%3E%3Cpath d='M10,50L70,70L150,50L190,90' stroke='%23000' fill='none' %3E%3C/path%3E%3C/svg%3E",
      getDefaults: (evaluation) => ({
        title: evaluation?.title || t("chartSuggestions.allSimulations"),
        chartSettings: {
          xAxis: "simulationRound",
          yAxis: "value"
        },
        dataToggles: {
          all: {
            simulation: true,
            simulationRound: true,
            target: false,
            resource: false
          }
        }
      })
    },
    {
      id: "all-targets",
      title: t("chartSuggestions.targetsCompared"),
      chartType: "radar",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3CclipPath id='c'%3E%3Ccircle cx='100' cy='50' r='40' fill='%23000'%3E%3C/circle%3E%3C/clipPath%3E%3Cg clip-path='url(%23c)'%3E%3Crect x='100' y='50' width='180' height='80' transform='rotate(72 100 50)' fill='%23444' %3E%3C/rect%3E%3Crect x='100' y='50' width='180' height='80' transform='rotate(144 100 50)' fill='%23888' %3E%3C/rect%3E%3Crect x='100' y='50' width='180' height='80' transform='rotate(216 100 50)' fill='%23AAA' %3E%3C/rect%3E%3Crect x='100' y='50' width='180' height='80' transform='rotate(0 100 50)' fill='%23ddd' %3E%3C/rect%3E%3Crect x='100' y='50' width='180' height='80' transform='rotate(-72 100 50)' fill='%23666' %3E%3C/rect%3E%3C/g%3E%3C/svg%3E",
      getDefaults: (evaluation) => ({
        title: evaluation?.title || t("chartSuggestions.targetsCompared"),
        chartSettings: {
          xAxis: "target",
          yAxis: "value",
          areasColored: true
        },
        data: {
          simulation: simulation ? [simulation.id] : []
        },
        dataToggles: {
          all: { target: true }
        }
      })
    },
    {
      id: "first-vs-last-round",
      title: t("chartSuggestions.startAndEnd"),
      chartType: "spider",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Cpath d='M63,63L100,90L139,62L124,18L76,18Z' stroke='%23CCC' fill='none' /%3E%3Cline x1='100' x2='100' y1='50' y2='90' stroke='%23AAA' /%3E%3Cline x1='100' x2='100' y1='50' y2='90' stroke='%23AAA' transform='rotate(72 100 50)' /%3E%3Cline x1='100' x2='100' y1='50' y2='90' stroke='%23AAA' transform='rotate(144 100 50)' /%3E%3Cline x1='100' x2='100' y1='50' y2='90' stroke='%23AAA' transform='rotate(216 100 50)' /%3E%3Cline x1='100' x2='100' y1='50' y2='90' stroke='%23AAA' transform='rotate(-72 100 50)' /%3E%3Cpath d='M80,57L100,70L130,60L105,44L91,38Z' stroke='%23000' fill='%23444444AF' /%3E%3Cpath d='M70,60L100,80L139,62L112,34L89,36Z' stroke='%23000' fill='%23555555AF' /%3E%3C/svg%3E",
      getDefaults: () => {
        const lastRoundIndex = rounds.length - 1;
        return {
          chartSettings: {
            xAxis: "target",
            yAxis: "value"
          },
          data: {
            simulation: simulation ? [simulation.id] : [],
            simulationRound: [lastRoundIndex, 0]
          },
          dataToggles: {
            all: { simulationRound: false, target: true }
          },
          itemConfigs: {
            simulationRound: {
              0: { color: "var(--color-primary-light)" },
              [lastRoundIndex]: { color: "var(--color-primary)" }
            }
          }
        };
      }
    },
    {
      id: "weakest-target",
      title: t("chartSuggestions.weakestTarget"),
      chartType: "line",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Cpath d='M10,50L50,30L140,90L190,80' stroke='%23000' fill='none' %3E%3C/path%3E%3C/svg%3E",
      getDefaults: () => {
        const resourceIds = simulation?.resourceIds || [];
        const resourceValues = resourceIds.map((id) => ({
          id: id,
          value: Number(lastRound?.elementValues?.[`resource_${id}`].value ?? 0)
        }));
        const weakestTargetValue = resourceValues.reduce(
          (
            weakest: { id: number; value: number } | null,
            targetPair: { id: number; value: number }
          ) => {
            if (weakest == null) return targetPair;
            if (weakest.value > targetPair.value) return targetPair;
            return weakest;
          },
          null
        );
        return {
          chartSettings: {
            xAxis: "simulationRound",
            yAxis: "value"
          },
          data: {
            target: weakestTargetValue
              ? [ensureInt(weakestTargetValue.id)]
              : [],
            simulation: simulation ? [simulation.id] : [],
            simulationRound: [0, rounds.length - 1]
          },
          dataToggles: {
            all: { simulationRound: true, target: false }
          }
        };
      }
    },
    {
      id: "strongest-target",
      title: t("chartSuggestions.strongestTarget"),
      chartType: "line",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Cpath d='M10,50L50,30L140,60L190,20' stroke='%23000' fill='none' %3E%3C/path%3E%3C/svg%3E",
      getDefaults: (evaluation) => {
        const targetIds = simulation?.targetIds?.slice() || [];
        const targetValues = targetIds.map((id) => ({
          id: id,
          value: Number(lastRound?.elementValues?.[`target_${id}`] ?? 0)
        }));

        const strongestTargetValue = targetValues.reduce(
          (
            strongest: { id: number; value: number } | null,
            targetPair: { id: number; value: number }
          ) => {
            if (strongest == null) return targetPair;
            if (strongest.value < targetPair.value) return targetPair;
            return strongest;
          },
          null
        );
        return {
          title: evaluation?.title || t("chartSuggestions.strongestTarget"),
          chartSettings: {
            xAxis: "simulationRound",
            yAxis: "value"
          },
          data: {
            target: strongestTargetValue ? [strongestTargetValue.id] : [],
            simulation: simulation ? [simulation.id] : [],
            simulationRound: [0, rounds.length - 1]
          },
          dataToggles: {
            all: { simulationRound: true, target: false }
          }
        };
      }
    },
    {
      id: "weakest-resource",
      title: t("chartSuggestions.weakestResource"),
      chartType: "line",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Cpath d='M10,50L50,30L140,90L190,80' stroke='%23000' fill='none' %3E%3C/path%3E%3C/svg%3E",
      getDefaults: () => {
        const resourceIds = simulation?.resourceIds || [];
        const resourceValues = resourceIds.map((id) => ({
          id: id,
          value: Number(lastRound?.elementValues?.[`resource_${id}`].value ?? 0)
        }));
        const weakestResourceValue = resourceValues.reduce(
          (
            weakest: { id: number; value: number } | null,
            resourcePair: { id: number; value: number }
          ) => {
            if (weakest == null) return resourcePair;
            if (weakest.value > resourcePair.value) return resourcePair;
            return weakest;
          },
          null
        );
        return {
          chartSettings: {
            xAxis: "simulationRound",
            yAxis: "value"
          },
          data: {
            resource: weakestResourceValue
              ? [ensureInt(weakestResourceValue.id)]
              : [],
            simulation: simulation ? [simulation.id] : [],
            simulationRound: [0, rounds.length - 1]
          },
          dataToggles: {
            all: { simulationRound: true, target: false, resource: false }
          }
        };
      }
    },
    {
      id: "strongest-resource",
      title: t("chartSuggestions.strongestResource"),
      chartType: "line",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100'%3E%3Cpath d='M10,50L50,30L140,60L190,20' stroke='%23000' fill='none' %3E%3C/path%3E%3C/svg%3E",
      getDefaults: (evaluation) => {
        const resourceIds = simulation?.resourceIds || [];
        const resourceValues = resourceIds.map((id) => ({
          id: id,
          value: Number(lastRound?.elementValues?.[`resource_${id}`].value ?? 0)
        }));
        const strongestResourceValue = resourceValues.reduce(
          (
            strongest: { id: number; value: number } | null,
            resourcePair: { id: number; value: number }
          ) => {
            if (strongest == null) return resourcePair;
            if (strongest.value < resourcePair.value) return resourcePair;
            return strongest;
          },
          null
        );
        return {
          title: evaluation?.title || t("chartSuggestions.strongestResource"),
          chartSettings: {
            xAxis: "simulationRound",
            yAxis: "value"
          },
          data: {
            resource: strongestResourceValue
              ? [ensureInt(strongestResourceValue.value)]
              : [],
            simulation: simulation ? [simulation.id] : [],
            simulationRound: [0, rounds.length - 1]
          },
          dataToggles: {
            all: { simulationRound: true, target: false, resource: false }
          }
        };
      }
    },
    {
      id: "action",
      title: t("chartSuggestions.actionEffects"),
      chartType: "bar",
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100' %3E%3Crect x='15' y='40' width='15' height='50' fill='%23888' /%3E%3Crect x='45' y='20' width='15' height='70' fill='%23888' /%3E%3Crect x='75' y='70' width='15' height='20' fill='%23888' /%3E%3Crect x='105' y='80' width='15' height='10' fill='%23888' /%3E%3Crect x='135' y='85' width='15' height='5' fill='%23888' /%3E%3Crect x='165' y='60' width='15' height='30' fill='%23888' /%3E%3C/svg%3E",
      getDefaults: (evaluation) => {
        const actions = useActionStore().items;
        const actionActivationIds = (simulation?.rounds || []).flatMap((r) =>
          r.activatedActions.map((a, i) => ({
            id: `${r.index}_${i}`,
            title: actions[a.actionId]?.title
          }))
        );
        const actionIdIndex = Math.floor(
          Math.random() * actionActivationIds.length
        );
        const actionActivation = actionActivationIds[actionIdIndex];

        let title = undefined;
        if (simulation != null) title = t("chartSuggestions.effects");
        if (evaluation?.title) title = evaluation.title;
        else if (actionActivation) title = `${actionActivation.title} - ${title}`;

        return {
          title: title,
          chartSettings: {
            xAxis: "target",
            yAxis: "effect",
            areasColored: false
          },
          data: {
            selectedItem: {
              type: "actionActivation",
              id: actionActivation?.id
            },
            actionActivation:
              actionActivation != null ? [actionActivation.id] : [],
            simulation: simulation ? [simulation.id] : [-1],
            simulationRound: [0, rounds.length - 1],
            selectedSimulationId: simulation ? simulation.id : -1
          },
          dataToggles: {
            all: { simulationRound: true, target: false }
          }
        };
      }
    },
    {
      id: "target-change",
      chartType: "bar",
      title: t("chartSuggestions.targetDevelopment"),
      img: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='100' viewBox='0 0 200 100' %3E%3Crect x='15' y='40' width='15' height='10' fill='%23888' /%3E%3Crect x='45' y='20' width='15' height='30' fill='%23888' /%3E%3Crect x='75' y='50' width='15' height='20' fill='%23888' /%3E%3Crect x='105' y='50' width='15' height='10' fill='%23888' /%3E%3Crect x='135' y='45' width='15' height='5' fill='%23888' /%3E%3Crect x='165' y='20' width='15' height='30' fill='%23888' /%3E%3C/svg%3E",
      getDefaults: (evaluation) => {
        const targetIds = simulation?.targetIds?.slice() || [];
        const targetId =
          targetIds[Math.floor(Math.random() * targetIds.length)];
        const targetTitle = useTargetStore().titles[targetId];
        return {
          title:
            evaluation?.title ||
            t("headings.targetStateDevelopment", [targetTitle]),
          chartSettings: {
            xAxis: "simulationRound",
            yAxis: "gain",
            areasColored: false
          },
          data: {
            selectedItem: {
              type: "target",
              id: targetId
            },
            simulation: simulation ? [simulation.id] : [],
            simulationRound: [0, rounds.length - 1]
          },
          dataToggles: {
            all: { simulationRound: true, target: false }
          }
        };
      }
    }
  ];

  return suggestions;
});
</script>

<style lang="scss">
.evaluation-chart-suggestions {
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 20rem));
    grid-auto-rows: 1fr;
    // display: flex;
    // flex-wrap: wrap;
    gap: 1rem;
    margin: 0 auto;
  }
  &__suggestion {
    display: block;
    max-width: 20rem;
    padding: 1rem;
    border-radius: 5px;
    outline: none;
    border: 1px solid var(--color-grey);
    box-shadow:
      0 2px 2px var(--color-shadow-1),
      inset 0 0 0 var(--color-shadow-1);
    transition: box-shadow 250ms;
    background-color: var(--color-background-close);

    img {
      pointer-events: none;
    }

    &:hover {
      box-shadow:
        0 2px 2px var(--color-shadow-3),
        inset 0 0 0 var(--color-shadow-1);
      background-color: var(--color-background-close-hover);
    }
    &:focus-visible {
      outline: 2px solid var(--color-action);
    }
    &:active {
      box-shadow:
        0 0px 0px var(--color-shadow-3),
        inset 0 2px 1px var(--color-shadow-1);
      transition-duration: 100ms;
    }
  }
}
</style>
